import { useState } from "react";
import { Upload, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Round 22 addendum · pending biometric submission banner.
 *
 * Admin lands on a draft pay-run that has
 * `biometric_pending_submission` set. The banner says who / when,
 * offers "Review & apply" (runs the normal apply flow with the
 * stashed text + the usual acknowledgements) and "Discard" (asks
 * for a required reason). After either action succeeds the parent
 * reloads the period; the banner disappears.
 */
export default function PendingBiometricBanner({ period, onCleared }) {
    const sub = period?.biometric_pending_submission;
    const [applying, setApplying] = useState(false);
    const [showDiscard, setShowDiscard] = useState(false);
    const [reason, setReason] = useState("");
    const [acked, setAcked] = useState(false);

    if (!sub || !sub.submitted_by) return null;

    const apply = async () => {
        setApplying(true);
        try {
            const r = await api.post(`/pay-periods/${period.id}/biometric/apply-pending`, {
                exception_resolutions: [],
                acknowledged_unconfirmed_rules: acked,
                acknowledged_row_count_deltas: acked,
                overwrite_manual_edits: false,
                text: "",   // ignored by server; stashed text wins
            });
            toast.success(`Applied — ${r.data?.lines_imported ?? "biometric"} saved`);
            onCleared?.();
        } catch (ex) {
            const detail = ex?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "unconfirmed_rules_ack_required") {
                setAcked(true);
                toast.warning("Rules still pending — tick the acknowledgement and retry.");
            } else if (detail && typeof detail === "object" && detail.code === "ppe_mismatch") {
                toast.error(detail.detail || "File PPE does not match the pay period");
            } else {
                toast.error(formatApiError(detail) || "Apply failed");
            }
        } finally {
            setApplying(false);
        }
    };

    const discard = async () => {
        if (!reason.trim()) {
            toast.warning("A short reason is required");
            return;
        }
        try {
            await api.post(`/pay-periods/${period.id}/biometric/discard-pending`, { reason: reason.trim() });
            toast.success("Discarded — audit entry recorded");
            setShowDiscard(false);
            setReason("");
            onCleared?.();
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Discard failed");
        }
    };

    return (
        <div data-testid="pending-biometric-banner" className="mb-6 border border-neutral-300 rounded-[3px] overflow-hidden">
            <div className="px-4 py-3 flex items-start gap-3" style={{ background: "#FFF8E6", borderBottom: "1px solid #E6D389" }}>
                <AlertTriangle size={18} className="mt-0.5 shrink-0" style={{ color: "#8A6D00" }} />
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-[13px] text-[color:var(--kp-charcoal)]">
                        Biometric upload from {sub.submitted_by} pending review
                    </div>
                    <div className="text-xs text-neutral-600 mt-0.5 font-mono-money">
                        Submitted {sub.submitted_at} · {sub.bytes} bytes · ppe check {sub.ppe_check || "skipped"}
                    </div>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                    <button
                        data-testid="pending-biometric-apply"
                        onClick={apply}
                        disabled={applying}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.1em] text-white rounded-[3px] disabled:opacity-50"
                        style={{ background: "var(--kp-red)" }}
                    >
                        <Upload size={13} /> Review &amp; apply
                    </button>
                    <button
                        data-testid="pending-biometric-discard"
                        onClick={() => setShowDiscard(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.1em] rounded-[3px] border border-neutral-300 bg-white text-[color:var(--kp-charcoal)]"
                    >
                        <X size={13} /> Discard
                    </button>
                </div>
            </div>
            {acked && (
                <div className="px-4 py-2 text-[11px] text-neutral-600" style={{ background: "#FFFCF2" }}>
                    Unconfirmed rules are being acknowledged on retry. Press Review &amp; apply again.
                </div>
            )}
            {showDiscard && (
                <div data-testid="pending-biometric-discard-dialog" className="px-4 py-3 bg-white">
                    <label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 font-display">Reason for discarding</label>
                    <textarea
                        data-testid="pending-biometric-discard-reason"
                        rows={2}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        className="kp-focus mt-1 w-full border border-neutral-300 px-3 py-2 text-xs bg-white rounded-[3px]"
                        placeholder="e.g. duplicate upload, wrong fortnight, test file"
                    />
                    <div className="mt-2 flex gap-2 justify-end">
                        <button
                            onClick={() => { setShowDiscard(false); setReason(""); }}
                            className="px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.1em] rounded-[3px] border border-neutral-300 bg-white"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="pending-biometric-discard-confirm"
                            onClick={discard}
                            disabled={!reason.trim()}
                            className="px-3 py-1.5 text-[12px] font-display font-semibold uppercase tracking-[0.1em] text-white rounded-[3px] disabled:opacity-50"
                            style={{ background: "var(--kp-red)" }}
                        >
                            Confirm discard
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
